import { DrupalNodeExternalResource } from "@/types"

import { AcquiaCliRenderer, AcquiaCliSidebarRenderer } from "@/components/app/acli/AcquiaCliRenderer"
import { PageInterface } from "@/components/page/PageInterface"
import { MetadataRoute } from "next"
import { ExternalResourceBase } from "@/components/page/external-resource"
import { AcquiaDamRenderer, AcquiaDamSidebarRenderer } from "@/components/app/dam/AcquiaDamRenderer"
import { parseStringPromise } from 'xml2js';
import { CommandSearch } from "@/components/app/dam/CommandSearch.client"

export interface CommandNamespace {
  command: string
  path: string
  help: string
  usage: string
}

export interface S3Object {
  Key: string;
  LastModified: string;
  ETag: string;
  Size: number;
  StorageClass: string;
}

interface ListBucketResult {
  Name: string;
  Prefix: string;
  KeyCount: number;
  MaxKeys: number;
  IsTruncated: boolean;
  Contents: S3Object[];
}

const requestOpts: NextFetchRequestConfig | RequestInit = {
  next: { revalidate: 3600 },
  // cache: 'force-cache'
}

export interface Command {
  name: string
  description: string
  usage: string[]
  help: string
  definition: {
    arguments: Record<string, Argument>
    options: Record<string, Option>
  }
  hidden: boolean
}

interface Argument {
  name: string
  is_required: boolean
  is_array: boolean
  description: string
  default: any
}

interface Option {
  name: string
  shortcut: string
  accept_value: boolean
  is_value_required: boolean
  is_multiple: boolean
  description: string
  default: any
}

export class JsonResource {
  constructor(public readonly indexEndpoint: string) {}

  getCommandEndpoint(command: string): string {
    const baseUrl = this.indexEndpoint.split('?')[0];
    return baseUrl + 'acquia-dam-ts-sdk/'+ command + '.md';
  }

  // async getCommandIndex ():Promise<CommandNamespace[]> {
  //   const response = await fetch(this.indexEndpoint, {
  //     next: { revalidate: 3600 },
  //   });
  //   return response.json();
  // }
  
  async getCommandIndex(): Promise<S3Object[]> {
    const response = await fetch(this.indexEndpoint);
    const xmlText = await response.text();
  
    try {
      const result: any = await parseStringPromise(xmlText, {
        explicitArray: false,
        mergeAttrs: true,
      });
  
      const listBucketResult: ListBucketResult = {
        Name: result.ListBucketResult.Name,
        Prefix: result.ListBucketResult.Prefix,
        KeyCount: parseInt(result.ListBucketResult.KeyCount),
        MaxKeys: parseInt(result.ListBucketResult.MaxKeys),
        IsTruncated: result.ListBucketResult.IsTruncated === 'true',
        Contents: [],
      };
  
      // Ensure Contents is always an array
      const contents = Array.isArray(result.ListBucketResult.Contents)
        ? result.ListBucketResult.Contents
        : [result.ListBucketResult.Contents];
  
      listBucketResult.Contents = contents.map((content: any) => ({
        Key: content.Key
        .replace('acquia-dam-ts-sdk/', '')
        .replace(/\.md$/, ''), // Remove the '.md' suffix from the Key
        LastModified: content.LastModified,
        ETag: content.ETag.replace(/^"|"$/g, ''), // Remove quotes from ETag
        Size: parseInt(content.Size),
        StorageClass: content.StorageClass,
      }));

      return listBucketResult.Contents;
    } catch (error) {
      console.error('Error parsing XML:', error);
      throw error;
    }
  }

  async getCommandData (command: string):Promise<string|null> {
    if (command.length == 0) {
      return null;
    }
    const request_uri = this.getCommandEndpoint(command)
    const response = await fetch(request_uri, {
      next: { revalidate: 3600 },
    });
    if (response.status != 200) {
      throw new Error("Command not found at: " + request_uri);
    }
    const mdString = await response.text();

    return mdString;
  }
}

export type DamSdkIndexContext = {
  index: S3Object[]
}

export class AcquiaDamResource extends ExternalResourceBase<DamSdkIndexContext>  {
  private readonly resource: JsonResource
  public readonly useDocumentSidebar = true
  public readonly useTableOfContents = true

  constructor(public readonly node: DrupalNodeExternalResource) {
    super()
    this.resource = new JsonResource(node.field_resource_url.uri);
  }

  get basepath(): string {
    return this.node.path.alias
  }

  getCommandFromSlug(slug: string): string|null {
    if (slug == this.basepath) {
      return null
    }
    return decodeURIComponent(`/${slug}`).replace(this.basepath + '/', '')
  }

  async getPageTitle(slug: string): Promise<string> {
    try {
      const commandName = this.getCommandFromSlug(slug)
      if (commandName == null) {
        return this.node.title
      }
      const data =  await this.resource.getCommandData(commandName)
      return data ? commandName : this.node.title;
    }
    catch (err) {
      return this.node.title
    }
  }

  async getHeaderChildren(slug: string): Promise<JSX.Element> {
    return <CommandSearch slugBase={this.basepath} />
  }

  async getContext(): Promise<DamSdkIndexContext> {
    return {
      index: await this.resource.getCommandIndex()
    }
  }

  async getPageContent(props: PageInterface<DrupalNodeExternalResource>): Promise<JSX.Element> {
    return <AcquiaDamRenderer {...props} />
  }

  async getSidebar(props: PageInterface<DrupalNodeExternalResource>): Promise<JSX.Element> {
    return <AcquiaDamSidebarRenderer {...props} />
  }

  async buildSitemap(baseUrl:string): Promise<MetadataRoute.Sitemap> {
    const index = await this.resource.getCommandIndex()
    return index.map((command) => ({
        url: baseUrl + this.basepath + '/' + command.Key,
        lastModified: new Date(),
        changeFrequency: 'weekly',
        priority: 0.8,
      }
    ))
  }
}